import { Button, HStack, Text } from "@chakra-ui/react"
import { flattenEdges } from "@pathwright/utils"
import Link, { LinkProps } from "@pathwright/ux/src/components/link/Link"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import {
  LicensorFragment,
  LicensorsQueryResult,
  useLicensorsQuery
} from "@pathwright/graphql-client/src/generated"
import { useMemo } from "react"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import SpaceIdentity from "../../space/identity/SpaceIdentity"
import SpaceTheme from "../../space/identity/SpaceTheme"
import { canEditLibrary } from "../../user/permissions"

function _massageStructuredLicensorsQueryData(
  licensorsQueryData: LicensorsQueryResult["data"]
): {
  licensors: LicensorFragment[]
  storeLinkLicensors: LicensorFragment[]
  curriculumSubscriptionLicensors: LicensorFragment[]
} {
  const storeLinks = flattenEdges(licensorsQueryData?.storeLinks || [])
  const curriculumSubscriptions = flattenEdges(
    licensorsQueryData?.school?.curriculum_subscriptions || []
  )

  const storeLinkLicensors = storeLinks.map(
    (storeLink) => storeLink.store.licensor
  )
  const curriculumSubscriptionLicensors = curriculumSubscriptions.map(
    (curriculumSubscription) => curriculumSubscription.licensor
  )

  const licensors = [...storeLinkLicensors, ...curriculumSubscriptionLicensors]

  return {
    licensors,
    storeLinkLicensors,
    curriculumSubscriptionLicensors
  }
}

export function massageLicensorsQueryData(
  licensorsQueryData: LicensorsQueryResult["data"]
): LicensorFragment[] {
  const licensorsData = _massageStructuredLicensorsQueryData(licensorsQueryData)
  return licensorsData.licensors
}

const _useLicensorsQuery = () => {
  const pwContext = usePathwrightContext()
  const licensorsQuery = useLicensorsQuery({
    // Both schools with curriculum subscriptions and schools with store links will have
    // `is_curriculum_subscriber` set to true. We can therefore skip the query for non-subscriber schools.
    skip:
      !pwContext.school.is_curriculum_subscriber || !canEditLibrary(pwContext)
  })

  return licensorsQuery
}

export const useLicensors = () => {
  const licensorsQuery = _useLicensorsQuery()

  const licensors = useMemo(() => {
    return massageLicensorsQueryData(licensorsQuery.data!)
  }, [licensorsQuery.data])

  return licensors
}

export const useStructuredLicensors = () => {
  const licensorsQuery = _useLicensorsQuery()

  const licensors = useMemo(() => {
    return _massageStructuredLicensorsQueryData(licensorsQuery.data!)
  }, [licensorsQuery.data])

  return [licensors, licensorsQuery] as const
}

export function LicensorLink({
  licensor,
  ...linkProps
}: { licensor: LicensorFragment } & LinkProps) {
  const { t } = useTranslate()
  const storeTo = `/library/store/${licensor.id}/`

  return (
    <SpaceTheme space={licensor}>
      <Button
        asChild
        colorPalette="brand" // the licensor's custom theme
        w="100%"
        borderRadius="20px"
      >
        <Link to={storeTo} variant="plain" textDecor="none" {...linkProps}>
          <HStack>
            <SpaceIdentity space={licensor} w="1.5rem" h="1.5rem" shadow="sm" />
            <Text as="span" color="inherit">
              {t("Get from {{ licensorSchoolName }}", {
                licensorSchoolName: licensor.name
              })}
            </Text>
          </HStack>
        </Link>
      </Button>
    </SpaceTheme>
  )
}

export function Licensors() {
  const licensors = useLicensors()

  return (
    <>
      {licensors.map((licensor) => (
        <LicensorLink key={licensor.id} licensor={licensor} />
      ))}
    </>
  )
}
